import { useState, useRef, useEffect, useCallback, CSSProperties } from "react";
import {
  X,
  Send,
  Loader2,
  Smile,
  Plus,
  ChevronLeft,
  MessageSquare,
  ImageIcon,
  Download,
  Mail,
  Maximize2,
  Minimize2,
  Volume2,
  VolumeX,
  Menu,
  ChevronUp,
  UserRoundPen,
} from "lucide-react";

// ============ Types ============
// Default API base URL (can be overridden for proxy setups)
const DEFAULT_API_BASE = "https://lucius-api-server-prod-prod.up.railway.app/api/v2";
const DEFAULT_SEND_URL = "https://lucius-bot-prod.up.railway.app/web/message";
// Default Lucius logo for avatar fallback
const DEFAULT_AVATAR_LOGO = "https://upplvnsliyltagqiylfv.supabase.co/storage/v1/object/public/widget-assets/default/icon-outline.svg";

// Mirrors lucius-bot's _WEB_HUMAN_TAKEOVER_WINDOW: once a staff reply
// is older than this, the backend resumes AI processing — the UI has
// to follow suit or visitors will keep seeing "Live support" while
// silently getting AI replies again.
const HUMAN_TAKEOVER_WINDOW_MS = 10 * 60 * 1000;

export interface ChatWidgetProps {
  /** Required: Widget ID to identify this widget instance */
  widgetId: string;
  /** Optional: Override API base URL (for proxy setups) */
  apiBaseUrl?: string;
  /** Optional: Override send URL (for proxy setups) */
  sendUrl?: string;
  /** Widget position */
  position?: "left" | "right";
  /** Header background color */
  headerColor?: string;
  /** Company name shown in header */
  companyName?: string;
  /** Company logo URL (used in header and message avatars) */
  companyLogo?: string;
  /** FAB (floating action button) logo URL - defaults to companyLogo */
  fabLogo?: string;
  /** Contact email for "Email us" button */
  contactEmail?: string;
  /** Welcome message shown to new users (overridden by server config if widgetId provided) */
  welcomeMessage?: string;
  /** Quick reply suggestions (overridden by server config if widgetId provided) */
  quickReplies?: string[];
  /** Notification sound URL */
  notificationSound?: string;
  /** Custom z-index for the widget */
  zIndex?: number;
}

interface WidgetConfig {
  widget_id: string;
  company_id: string;
  company_name?: string;
  /** Optional override for the name shown in the header / message bubbles.
   * Server returns this when the商家 set a custom display name in their
   * dashboard; SDK falls back to company_name when absent. */
  display_name?: string | null;
  welcome_message?: string;
  quick_replies?: string[];
  fab_logo_url?: string;
  avatar_url?: string;
  /** Contact email for "Email us" menu item. Empty / null hides the entry. */
  contact_email?: string | null;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  image?: string;
  senderName?: string;
  avatarUrl?: string;
  isHuman?: boolean;
}

interface Conversation {
  id: string;
  sessionId: string | null;
  title: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
}

/** Local fallback for "is a staff reply still inside the takeover
 * window?". Replaced by server-authoritative X-Takeover-Until /
 * X-Server-Now headers once any poll completes, but kept so the UI
 * doesn't show the wrong state during the very first second before
 * the first poll returns. */
function isHumanActive(messages: Message[]): boolean {
  let latest = 0;
  for (const m of messages) {
    if (!m.isHuman) continue;
    const ts = new Date(m.timestamp).getTime();
    if (!Number.isNaN(ts) && ts > latest) latest = ts;
  }
  return latest > 0 && Date.now() - latest < HUMAN_TAKEOVER_WINDOW_MS;
}

/** Captured from the message poll endpoint's X-Server-Now and
 * X-Takeover-Until headers. Using both lets the widget setTimeout
 * to expiry in pure server-clock space, immune to a misconfigured
 * visitor system clock. */
type TakeoverInfo = { takeoverUntil: string; serverNow: string };

// ============ Constants ============
const POLL_INTERVAL_MS = 1500;
const BACKGROUND_POLL_INTERVAL_MS = 3000;
const FETCH_MISSED_LIMIT = 50;
const NO_REPLY_RESPONSE = "NO_REPLY";
const NO_REPLY_REPLACEMENT = "How can I assist you?";

const EMOJI_LIST = [
  "😀", "😊", "😄", "😁", "😅", "😂", "🤣", "😉",
  "😍", "🥰", "😘", "😋", "😎", "🤔", "🤗", "🤩",
  "👍", "👋", "🙏", "💪", "🎉", "🔥", "❤️", "💯",
  "✅", "⭐", "💡", "🚀", "👀", "📝", "💬", "🤖",
];

// ============ Styles ============
const styles: Record<string, CSSProperties> = {
  // Container
  windowContainer: {
    position: "fixed",
    top: "auto",
    left: "auto",
    bottom: 96,
    width: 380,
    maxWidth: "calc(100vw - 48px)",
    transition: "all 0.3s ease-out",
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Helvetica Neue", "Segoe UI", Arial, Roboto, sans-serif',
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textAlign: "left" as const,  // Prevent parent CSS from affecting widget
  },
  windowContainerFullscreen: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100dvh",
    maxWidth: "none",
  },
  windowContainerOpen: {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto" as const,
  },
  windowContainerClosed: {
    opacity: 0,
    transform: "translateY(16px)",
    pointerEvents: "none" as const,
  },
  // Window
  window: {
    background: "white",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
    border: "1px solid #e5e5e5",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
    height: 500,
    maxHeight: "calc(100vh - 150px)",
  },
  windowFullscreen: {
    width: "100%",
    height: "100%",
    maxHeight: "100%",
    borderRadius: 0,
  },
  // Header
  header: {
    padding: 16,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    color: "white",
    fontWeight: 600,
    fontSize: 16,
    margin: 0,
  },
  headerSubtitle: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 13,
    margin: "2px 0 0 0",
  },
  backBtn: {
    background: "transparent",
    border: "none",
    color: "rgba(255,255,255,0.8)",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
  },
  avatarContainer: {
    position: "relative" as const,
    width: 40,
    height: 40,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  avatarImgDefault: {
    width: 28,
    height: 28,
    objectFit: "contain" as const,
  },
  avatarImgCustom: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    objectFit: "cover" as const,
  },
  avatarStatus: {
    position: "absolute" as const,
    bottom: -2,
    right: -2,
    width: 12,
    height: 12,
    background: "#22c55e",
    borderRadius: "50%",
    border: "2px solid",
  },
  headerInfo: {
    flex: 1,
  },
  // Menu
  menuContainer: {
    position: "relative" as const,
  },
  menuBtn: {
    background: "transparent",
    border: "none",
    color: "white",
    cursor: "pointer",
    padding: "8px 12px",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    transition: "background 0.15s ease",
  },
  menuBtnOpen: {
    background: "rgba(255,255,255,0.2)",
  },
  menuDropdown: {
    position: "absolute" as const,
    top: "calc(100% + 8px)",
    right: 0,
    background: "white",
    border: "1px solid #e5e5e5",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    overflow: "hidden",
    minWidth: 160,
    zIndex: 10,
  },
  menuItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    background: "transparent",
    border: "none",
    color: "#646566",
    fontSize: 14,
    cursor: "pointer",
    textAlign: "left" as const,
  },
  // Conversation list
  conversationList: {
    flex: 1,
    overflowY: "auto" as const,
  },
  conversationItem: {
    width: "100%",
    padding: 16,
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "transparent",
    border: "none",
    borderBottom: "1px solid #e5e5e5",
    cursor: "pointer",
    textAlign: "left" as const,
  },
  conversationItemActive: {
    background: "#f5f5f5",
  },
  conversationIcon: {
    color: "#646566",
    flexShrink: 0,
  },
  conversationInfo: {
    flex: 1,
    minWidth: 0,
  },
  conversationTitle: {
    color: "#323233",
    fontSize: 14,
    fontWeight: 500,
    margin: 0,
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  conversationTime: {
    color: "#646566",
    fontSize: 12,
    margin: "4px 0 0 0",
  },
  newConversationContainer: {
    padding: 16,
  },
  newConversationBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "12px 16px",
    border: "none",
    borderRadius: 12,
    color: "white",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  // Messages
  messagesContainer: {
    flex: 1,
    overflowY: "auto" as const,
    padding: 16,
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
  },
  messageRow: {
    display: "flex",
  },
  messageRowUser: {
    justifyContent: "flex-end",
  },
  messageRowAssistant: {
    justifyContent: "flex-start",
  },
  assistantMessageWrapper: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    maxWidth: "85%",
  },
  messageAvatarDefault: {
    width: 20,
    height: 20,
    padding: 4,
    boxSizing: "content-box" as const,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 20,
    background: "#ffffff",
    objectFit: "contain" as const,
  },
  messageAvatarCustom: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 20,
    objectFit: "cover" as const,
  },
  messageContent: {
    minWidth: 0,
  },
  messageSender: {
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 4,
  },
  messageBubble: {
    borderRadius: 16,
    padding: "10px 14px",
  },
  messageBubbleAssistant: {
    background: "#f3f4f6",
    color: "#323233",
    borderTopLeftRadius: 4,
  },
  messageBubbleUser: {
    color: "white",
    borderBottomRightRadius: 4,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 1.625,
    margin: 0,
    whiteSpace: "pre-wrap" as const,
    wordWrap: "break-word" as const,
    color: "inherit",
  },
  messageLink: {
    color: "#6366f1",
    textDecoration: "underline",
    wordBreak: "break-all" as const,
  },
  messageImage: {
    maxWidth: "100%",
    maxHeight: 160,
    borderRadius: 8,
    marginBottom: 8,
  },
  imagePlaceholder: {
    background: "#e5e5e5",
    borderRadius: 8,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  // Quick replies
  quickRepliesContainer: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 8,
    marginTop: 8,
  },
  quickReplyBtn: {
    padding: "8px 12px",
    fontSize: 14,
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    cursor: "pointer",
    color: "#323233",
  },
  // Loading
  loadingBubble: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  loadingText: {
    fontSize: 12,
    color: "#646566",
  },
  loadingDots: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  loadingDot: {
    width: 6,
    height: 6,
    background: "#646566",
    borderRadius: "50%",
    animation: "lcw-bounce 1s infinite",
  },
  // Divider
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "12px 0",
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: "#e5e5e5",
  },
  dividerText: {
    fontSize: 12,
    color: "#646566",
    whiteSpace: "nowrap" as const,
  },
  // Input area
  inputArea: {
    padding: 16,
    borderTop: "1px solid #e5e5e5",
  },
  imagePreview: {
    position: "relative" as const,
    display: "inline-block",
    marginBottom: 8,
  },
  previewImg: {
    maxHeight: 80,
    borderRadius: 8,
  },
  imageUploadingOverlay: {
    position: "absolute" as const,
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  removeImageBtn: {
    position: "absolute" as const,
    top: -8,
    right: -8,
    width: 20,
    height: 20,
    background: "#ef4444",
    border: "none",
    borderRadius: "50%",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#f5f5f5",
    borderRadius: 12,
    padding: "8px 12px",
  },
  fileInput: {
    display: "none",
  },
  iconBtn: {
    background: "transparent",
    border: "none",
    color: "#646566",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  textInput: {
    flex: 1,
    background: "transparent",
    border: "none",
    color: "#323233",
    fontSize: 16,
    outline: "none",
    fontFamily: "inherit",
  },
  emojiContainer: {
    position: "relative" as const,
  },
  emojiPicker: {
    position: "absolute" as const,
    bottom: "calc(100% + 8px)",
    right: 0,
    background: "white",
    border: "1px solid #e5e5e5",
    borderRadius: 12,
    padding: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    width: 280,
    display: "grid",
    gridTemplateColumns: "repeat(8, 1fr)",
    gap: 4,
    zIndex: 10,
  },
  emojiBtn: {
    background: "transparent",
    border: "none",
    fontSize: 20,
    padding: 4,
    cursor: "pointer",
    borderRadius: 4,
  },
  sendBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  poweredBy: {
    display: "block",
    textAlign: "center" as const,
    color: "#969799",
    fontSize: 12,
    margin: "8px 0 0 0",
    textDecoration: "none",
  },
  // FAB
  fabContainer: {
    position: "fixed" as const,
    bottom: 24,
    zIndex: 9999,
  },
  fab: {
    position: "relative" as const,
    width: 56,
    height: 56,
    borderRadius: "50%",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  fabLogoDefault: {
    width: 36,
    height: 36,
    objectFit: "contain" as const,
    pointerEvents: "none" as const,
  },
  fabLogoCustom: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    objectFit: "cover" as const,
    pointerEvents: "none" as const,
  },
};

// CSS keyframes (injected once)
const injectStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("lcw-styles")) return;

  // Load Inter font from Google Fonts
  const fontLink = document.createElement("link");
  fontLink.rel = "stylesheet";
  fontLink.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap";
  document.head.appendChild(fontLink);

  const style = document.createElement("style");
  style.id = "lcw-styles";
  style.textContent = `
    @keyframes lcw-bounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-6px); }
    }
    @keyframes lcw-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .lcw-powered-by { transition: color 0.15s; }
    .lcw-powered-by:hover { color: #4a4a4a !important; }
  `;
  document.head.appendChild(style);
};

// ============ Helpers ============
const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Render text with clickable URLs
const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const renderTextWithLinks = (text: string, linkColor?: string): (string | JSX.Element)[] => {
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...styles.messageLink, color: linkColor || styles.messageLink.color }}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

const createWelcomeMessage = (content: string): Message => ({
  id: "welcome",
  role: "assistant",
  content,
  timestamp: new Date().toISOString(),
});

const createNewConversation = (welcomeMessage: string): Conversation => ({
  id: generateId(),
  sessionId: null,
  title: "New conversation",
  messages: [createWelcomeMessage(welcomeMessage)],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const sortByUpdatedAt = (convs: Conversation[]) =>
  [...convs].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

const formatTime = (isoString: string) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// ============ Hooks ============

const MOBILE_BREAKPOINT = 640;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return isMobile;
}

type VisualViewportInfo = { height: number; offsetTop: number } | null;

function useVisualViewport(): VisualViewportInfo {
  const [info, setInfo] = useState<VisualViewportInfo>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handler = () => setInfo({ height: vv.height, offsetTop: vv.offsetTop });
    vv.addEventListener("resize", handler);
    vv.addEventListener("scroll", handler);
    handler();
    return () => {
      vv.removeEventListener("resize", handler);
      vv.removeEventListener("scroll", handler);
    };
  }, []);

  return info;
}

// ============ Component ============
export function ChatWidget({
  widgetId,
  apiBaseUrl,
  sendUrl: propSendUrl,
  position = "right",
  headerColor = "#1a1a2e",
  companyName: propCompanyName = "Support",
  companyLogo,
  fabLogo,
  contactEmail,
  welcomeMessage: propWelcomeMessage = "Hi! How can we help you today?",
  quickReplies: propQuickReplies = [],
  notificationSound,
  zIndex = 9998,
}: ChatWidgetProps) {
  // SSR / hydration safety: render nothing on the server and on the very
  // first client render, then flip to mounted in an effect. This way both
  // the server-rendered HTML and React's first client render output the
  // same (empty) tree — no hydration mismatch — and the actual widget
  // appears once useEffect runs in the browser. Without this, frameworks
  // like Next.js render the widget with default props on the server and
  // re-render with widgetId-fetched config on the client, triggering a
  // React hydration error.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Compute actual URLs (props override defaults)
  const baseUrl = apiBaseUrl || DEFAULT_API_BASE;
  const sendUrl = propSendUrl || DEFAULT_SEND_URL;
  const pollUrl = `${baseUrl}/platform-messages/website`;
  const configUrl = `${baseUrl}/widget`;

  // Inject CSS
  useEffect(() => { injectStyles(); }, []);

  // Storage keys (unique per widget)
  const STORAGE_KEY = `lcw-conversations-${widgetId}`;
  const SOUND_KEY = `lcw-sound-${widgetId}`;
  const EMAIL_KEY = `lcw-email-${widgetId}`;

  // Mobile detection & viewport
  const isMobile = useIsMobile();
  const viewport = useVisualViewport();

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [fabHovered, setFabHovered] = useState(false);
  const [showConversationList, setShowConversationList] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [loadingConversationIds, setLoadingConversationIds] = useState<Set<string>>(new Set());
  const [showEmoji, setShowEmoji] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    previewUrl: string;
    mimeType: string;
    fileName: string;
    uploading: boolean;
    uploaded?: { url: string; storagePath: string; size: number };
  } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [hasFetchedMissed, setHasFetchedMissed] = useState(false);
  const [visitorEmail, setVisitorEmail] = useState<string | null>(() => {
    try { return localStorage.getItem(EMAIL_KEY) || null; } catch { return null; }
  });
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [takeoverInfo, setTakeoverInfo] = useState<TakeoverInfo | null>(null);
  // Cache key for widget config
  const CONFIG_CACHE_KEY = `lcw-config-${widgetId}`;

  // Load cached config on init to prevent flash
  const [serverConfig, setServerConfig] = useState<WidgetConfig | null>(() => {
    if (!widgetId || typeof window === "undefined") return null;
    try {
      const cached = localStorage.getItem(CONFIG_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [configLoaded, setConfigLoaded] = useState(!widgetId); // If no widgetId, config is "loaded"

  // Fetch widget config from server
  useEffect(() => {
    if (!widgetId) {
      setConfigLoaded(true);
      return;
    }

    const fetchConfig = async () => {
      try {
        const url = `${configUrl}/${encodeURIComponent(widgetId)}/config`;
        const response = await fetch(url);
        if (response.ok) {
          const config = await response.json();
          setServerConfig(config);
          // Cache config to prevent flash on next load
          try {
            localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(config));
          } catch {
            // Ignore localStorage errors
          }
        }
      } catch (error) {
        console.error("[ChatWidget] Failed to fetch config:", error);
      } finally {
        setConfigLoaded(true);
      }
    };

    fetchConfig();
  }, [widgetId, configUrl, CONFIG_CACHE_KEY]);

  // Use server config if available, otherwise use props.
  // Name precedence: server display_name (商家自定义) → server company_name → prop default
  const companyName =
    serverConfig?.display_name || serverConfig?.company_name || propCompanyName;
  const welcomeMessage = serverConfig?.welcome_message || propWelcomeMessage;
  const quickReplies = serverConfig?.quick_replies || propQuickReplies;
  // Server config logos override props (fab_logo_url -> fabLogo, avatar_url -> companyLogo)
  const effectiveFabLogo = serverConfig?.fab_logo_url || fabLogo;
  const effectiveCompanyLogo = serverConfig?.avatar_url || companyLogo;
  const effectiveContactEmail = serverConfig?.contact_email || contactEmail;

  // Loading helpers (per-conversation)
  const isLoading = currentConversationId ? loadingConversationIds.has(currentConversationId) : false;
  const setConversationLoading = useCallback((convId: string, loading: boolean) => {
    setLoadingConversationIds((prev) => {
      const next = new Set(prev);
      if (loading) next.add(convId); else next.delete(convId);
      return next;
    });
  }, []);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
  const pollAbortMapRef = useRef<Map<string, AbortController>>(new Map());
  const sendMessageRef = useRef<(text?: string) => void>(() => {});

  // Derived
  const currentConversation = conversations.find((c) => c.id === currentConversationId);
  const messages = currentConversation?.messages || [];
  // Active = backend says we're inside the takeover window (header-driven)
  // OR local fallback for the moment before the first poll returns. The
  // server-authoritative side wins; isHumanActive only matters during the
  // initial render before any poll has come back.
  const humanActive = takeoverInfo != null || isHumanActive(messages);
  const lastHumanAgent = humanActive ? [...messages].reverse().find(m => m.isHuman) : null;

  // Helper: pull X-Server-Now / X-Takeover-Until from any poll response.
  // X-Server-Now is always present on supported backends, so its absence
  // means "old backend, leave state alone and fall back to local".
  const applyTakeoverHeaders = useCallback((response: Response) => {
    const serverNow = response.headers.get("X-Server-Now");
    if (!serverNow) return;
    const takeoverUntil = response.headers.get("X-Takeover-Until");
    setTakeoverInfo(takeoverUntil ? { takeoverUntil, serverNow } : null);
  }, []);

  // Schedule a re-render at the exact expiry instant — computed in pure
  // server-clock space so a misconfigured visitor system clock can't
  // shift it. setTimeout itself uses elapsed wall time, immune to
  // visitor clock drift.
  useEffect(() => {
    if (!takeoverInfo) return;
    const ms =
      new Date(takeoverInfo.takeoverUntil).getTime() -
      new Date(takeoverInfo.serverNow).getTime();
    if (ms <= 0) {
      setTakeoverInfo(null);
      return;
    }
    const id = setTimeout(() => setTakeoverInfo(null), ms);
    return () => clearTimeout(id);
  }, [takeoverInfo]);

  // Conversation switches restart takeover state — the new conversation
  // hasn't been polled yet so any prior window is stale by definition.
  useEffect(() => {
    setTakeoverInfo(null);
  }, [currentConversationId]);

  // Load conversations on mount (wait for config to load first)
  useEffect(() => {
    // Wait for server config to load before initializing conversations
    if (!configLoaded) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Conversation[];
        const sorted = sortByUpdatedAt(parsed);
        setConversations(sorted);
        if (sorted.length > 0) {
          setCurrentConversationId(sorted[0].id);
        }
      } catch {
        const newConv = createNewConversation(welcomeMessage);
        setConversations([newConv]);
        setCurrentConversationId(newConv.id);
      }
    } else {
      const newConv = createNewConversation(welcomeMessage);
      setConversations([newConv]);
      setCurrentConversationId(newConv.id);
    }

    const soundPref = localStorage.getItem(SOUND_KEY);
    if (soundPref !== null) {
      setSoundEnabled(soundPref === "true");
    }

    if (notificationSound) {
      notificationSoundRef.current = new Audio(notificationSound);
      notificationSoundRef.current.volume = 0.3;
    }
  }, [STORAGE_KEY, SOUND_KEY, welcomeMessage, notificationSound, configLoaded]);

  // Fetch missed messages
  useEffect(() => {
    if (hasFetchedMissed || conversations.length === 0) return;

    const fetchMissedForConversation = async (conv: Conversation): Promise<Message[]> => {
      if (!conv.sessionId) return [];
      const lastUserMessage = [...conv.messages].reverse().find((m) => m.role === "user" && m.id !== "welcome");
      if (!lastUserMessage) return [];

      try {
        const url = `${pollUrl}?sessionid=${encodeURIComponent(conv.sessionId)}&message_id=${encodeURIComponent(lastUserMessage.id)}&after_limit=${FETCH_MISSED_LIMIT}`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!response.ok) return [];
        applyTakeoverHeaders(response);

        const data = await response.json();
        const existingIds = new Set(conv.messages.map((m) => m.id));

        return Array.isArray(data)
          ? data
              .filter((msg: { message_id?: string; sender_role_id?: number; sender_role?: string; message_role?: string }) =>
                msg.message_role !== "user" && msg.sender_role !== "user" && msg.sender_role_id !== 1 && msg.message_id && !existingIds.has(msg.message_id)
              )
              .map((msg: { message_id: string; content: string; timestamp: string; message_role?: string; raw?: { username?: string; avatar_url?: string } | null }) => {
                let content = msg.content || "...";
                if (content === NO_REPLY_RESPONSE) content = NO_REPLY_REPLACEMENT;
                const isHuman = msg.message_role === "staff";
                return {
                  id: msg.message_id,
                  role: "assistant" as const,
                  content,
                  timestamp: msg.timestamp || new Date().toISOString(),
                  isHuman,
                  senderName: isHuman ? msg.raw?.username : undefined,
                  avatarUrl: isHuman ? msg.raw?.avatar_url : undefined,
                };
              })
          : [];
      } catch {
        return [];
      }
    };

    const fetchAllMissed = async () => {
      const withSession = conversations.filter((c) => c.sessionId);
      if (withSession.length === 0) {
        setHasFetchedMissed(true);
        return;
      }

      let hasUpdates = false;
      const recentCutoff = Date.now() - 24 * 60 * 60 * 1000;
      const eligibleIds = new Set(withSession.filter((c) => new Date(c.updatedAt).getTime() > recentCutoff).map((c) => c.sessionId));

      const updated = await Promise.all(
        conversations.map(async (conv) => {
          if (!eligibleIds.has(conv.sessionId)) return conv;
          const missed = await fetchMissedForConversation(conv);
          if (missed.length > 0) {
            hasUpdates = true;
            playNotificationSound();
            return { ...conv, messages: [...conv.messages, ...missed], updatedAt: new Date().toISOString() };
          }
          return conv;
        })
      );

      if (hasUpdates) setConversations(sortByUpdatedAt(updated));
      setHasFetchedMissed(true);
    };

    fetchAllMissed();
  }, [conversations, hasFetchedMissed, pollUrl]);

  // Save conversations
  useEffect(() => {
    if (conversations.length > 0) {
      const prepared = conversations.map((conv) => ({
        ...conv,
        messages: conv.messages.map((msg) => ({ ...msg, image: msg.image ? "[image]" : undefined })),
      }));

      let toSave = prepared;
      while (toSave.length > 0) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
          break;
        } catch {
          if (toSave.length <= 1) {
            localStorage.removeItem(STORAGE_KEY);
            break;
          }
          toSave = toSave.slice(0, -1);
        }
      }
    }
  }, [conversations, STORAGE_KEY]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentConversationId, conversations]);

  // Focus input
  useEffect(() => {
    if (isOpen && !showConversationList) inputRef.current?.focus();
  }, [isOpen, showConversationList]);

  // Close popups on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setShowEmoji(false);
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Warn before unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (loadingConversationIds.size > 0) {
        e.preventDefault();
        e.returnValue = "AI is still responding. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [loadingConversationIds]);

  // Mobile: auto-fullscreen when widget opens
  useEffect(() => {
    if (isMobile && isOpen) {
      setIsFullscreen(true);
    }
    if (isMobile && !isOpen) {
      setIsFullscreen(false);
    }
  }, [isMobile, isOpen]);

  // Lock body scroll when mobile fullscreen is open
  useEffect(() => {
    if (isMobile && isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobile, isOpen]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      pollAbortMapRef.current.forEach((controller) => controller.abort());
      pollAbortMapRef.current.clear();
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled || !notificationSoundRef.current) return;
    try {
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current.play().catch(() => {});
    } catch {}
  }, [soundEnabled]);

  const insertEmoji = (emoji: string) => {
    setInput((prev) => prev + emoji);
    inputRef.current?.focus();
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image size should be less than 5MB"); return; }

    const previewUrl = URL.createObjectURL(file);
    setSelectedImage((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return { previewUrl, mimeType: file.type, fileName: file.name, uploading: true };
    });

    try {
      const uploadUrl = sendUrl.replace(/\/message$/, "/upload-image");
      const params = new URLSearchParams();
      if (widgetId) params.set("widgetId", widgetId);
      const sid = currentConversation?.sessionId;
      if (sid) params.set("sessionId", sid);
      const target = params.toString() ? `${uploadUrl}?${params.toString()}` : uploadUrl;

      const response = await fetch(target, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
          "X-File-Name": encodeURIComponent(file.name),
        },
        body: file,
      });
      if (!response.ok) throw new Error(`upload failed (${response.status})`);
      const data = await response.json();
      if (!data.ok || !data.url) throw new Error(data.error || "upload failed");

      setSelectedImage((prev) =>
        prev && prev.previewUrl === previewUrl
          ? {
              ...prev,
              uploading: false,
              uploaded: { url: data.url, storagePath: data.storagePath, size: data.size },
            }
          : prev
      );
    } catch (err) {
      console.error("Image upload error:", err);
      alert("Image upload failed. Please try again.");
      URL.revokeObjectURL(previewUrl);
      setSelectedImage((prev) => (prev && prev.previewUrl === previewUrl ? null : prev));
    }
  };

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem(SOUND_KEY, String(newValue));
  };

  const exportConversation = () => {
    if (!currentConversation) return;
    const data = messages.map((msg) => {
      const time = new Date(msg.timestamp).toLocaleString();
      const role = msg.role === "user" ? "You" : (msg.isHuman ? (msg.senderName || "Agent") : companyName);
      const img = msg.image && msg.image !== "[image]" ? "\n[Image attached]" : "";
      return `[${time}] ${role}:\n${msg.content}${img}`;
    }).join("\n\n---\n\n");

    const blob = new Blob([data], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chat-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openEmailContact = () => {
    if (!effectiveContactEmail) return;
    const subject = encodeURIComponent(`Question about ${companyName}`);
    const body = encodeURIComponent(`Hi ${companyName} team,\n\nI have a question:\n\n`);
    window.open(`mailto:${effectiveContactEmail}?subject=${subject}&body=${body}`, "_blank");
  };

  const updateConversation = useCallback((conversationId: string, updates: Partial<Conversation>) => {
    setConversations((prev) => {
      const updated = prev.map((conv) => conv.id === conversationId ? { ...conv, ...updates, updatedAt: new Date().toISOString() } : conv);
      return sortByUpdatedAt(updated);
    });
  }, []);

  const startNewConversation = () => {
    const newConv = createNewConversation(welcomeMessage);
    setConversations((prev) => sortByUpdatedAt([newConv, ...prev]));
    setCurrentConversationId(newConv.id);
    setShowConversationList(false);
  };

  const selectConversation = (id: string) => {
    setCurrentConversationId(id);
    setShowConversationList(false);
  };

  // Poll for response
  const pollForResponse = useCallback(async (
    sessionId: string,
    lastMessageId: string,
    updatedMessages: Message[],
    conversationId: string,
  ): Promise<void> => {
    const hasHuman = isHumanActive(updatedMessages);
    if (hasHuman) {
      setConversationLoading(conversationId, false);
      return;
    }

    const abortController = new AbortController();
    pollAbortMapRef.current.set(conversationId, abortController);

    while (true) {
      if (abortController.signal.aborted) return;

      try {
        const url = `${pollUrl}?sessionid=${encodeURIComponent(sessionId)}&message_id=${encodeURIComponent(lastMessageId)}&after_limit=10`;
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok) throw new Error("Failed to poll");
        applyTakeoverHeaders(response);

        const data = await response.json();
        const aiMessages = Array.isArray(data) ? data.filter((msg: { sender_role_id?: number; sender_role?: string; message_role?: string }) =>
          msg.message_role !== "user" && msg.sender_role !== "user" && msg.sender_role_id !== 1
        ) : [];

        if (aiMessages.length > 0) {
          const latest = aiMessages[aiMessages.length - 1];
          let content = latest.content || "...";
          if (content === NO_REPLY_RESPONSE) content = NO_REPLY_REPLACEMENT;
          const isHuman = latest.message_role === "staff";

          const assistantMsg: Message = {
            id: latest.message_id || generateId(),
            role: "assistant",
            content,
            timestamp: latest.timestamp || new Date().toISOString(),
            isHuman,
            senderName: isHuman ? latest.raw?.username : undefined,
            avatarUrl: isHuman ? latest.raw?.avatar_url : undefined,
          };

          updateConversation(conversationId, { messages: [...updatedMessages, assistantMsg] });
          playNotificationSound();
          setConversationLoading(conversationId, false);
          pollAbortMapRef.current.delete(conversationId);
          return;
        }

        // Agent 选择不回复 (SILENT/SUSPENDED) 时不会有 assistant 消息落库，
        // 检测当前 user 消息的 message_status：若已 completed/failed 说明本轮处理结束，停止轮询避免死循环。
        const userMessageStatus = Array.isArray(data)
          ? data.find((msg: { message_id?: string; message_status?: string }) => msg.message_id === lastMessageId)?.message_status
          : undefined;
        if (userMessageStatus === "completed" || userMessageStatus === "failed") {
          setConversationLoading(conversationId, false);
          pollAbortMapRef.current.delete(conversationId);
          return;
        }

        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          setConversationLoading(conversationId, false);
          pollAbortMapRef.current.delete(conversationId);
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
    }
  }, [pollUrl, updateConversation, playNotificationSound, setConversationLoading]);

  // Background polling
  useEffect(() => {
    const sessionId = currentConversation?.sessionId;
    if (!isOpen || !sessionId || !currentConversationId) return;

    const intervalId = setInterval(async () => {
      if (isLoading) return;
      const lastUserMsg = [...(currentConversation?.messages || [])].reverse().find((m) => m.role === "user" && m.id !== "welcome");
      if (!lastUserMsg) return;

      try {
        const url = `${pollUrl}?sessionid=${encodeURIComponent(sessionId)}&message_id=${encodeURIComponent(lastUserMsg.id)}&after_limit=10`;
        const response = await fetch(url);
        if (!response.ok) return;
        applyTakeoverHeaders(response);

        const data = await response.json();
        if (!Array.isArray(data) || data.length === 0) return;

        const existingIds = new Set((currentConversation?.messages || []).map((m) => m.id));
        const newMsgs = data
          .filter((msg: { message_id?: string; sender_role_id?: number; sender_role?: string; message_role?: string }) =>
            msg.message_role !== "user" && msg.sender_role !== "user" && msg.sender_role_id !== 1 && msg.message_id && !existingIds.has(msg.message_id)
          )
          .map((msg: { message_id: string; content: string; timestamp: string; message_role?: string; raw?: { username?: string; avatar_url?: string } | null }) => {
            let content = msg.content || "...";
            if (content === NO_REPLY_RESPONSE) content = NO_REPLY_REPLACEMENT;
            const isHuman = msg.message_role === "staff";
            return {
              id: msg.message_id,
              role: "assistant" as const,
              content,
              timestamp: msg.timestamp || new Date().toISOString(),
              isHuman,
              senderName: isHuman ? msg.raw?.username : undefined,
              avatarUrl: isHuman ? msg.raw?.avatar_url : undefined,
            };
          });

        if (newMsgs.length > 0) {
          updateConversation(currentConversationId, { messages: [...(currentConversation?.messages || []), ...newMsgs] });
          playNotificationSound();
        }
      } catch {}
    }, BACKGROUND_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isOpen, currentConversationId, currentConversation?.sessionId, currentConversation?.messages, loadingConversationIds, pollUrl, updateConversation, playNotificationSound]);

  // Email submit handler
  const handleEmailSubmit = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) {
      setEmailError("Please enter your email");
      return;
    }
    if (trimmed.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError("Please enter a valid email");
      return;
    }
    setVisitorEmail(trimmed);
    setEmailError("");
    try { localStorage.setItem(EMAIL_KEY, trimmed); } catch {}
  };

  // Send message
  const sendMessage = async (quickReplyText?: string) => {
    const messageText = quickReplyText || input.trim();
    const readyImage = selectedImage?.uploaded ? selectedImage : null;
    if ((!messageText && !readyImage) || isLoading || !currentConversationId) return;
    if (selectedImage && !readyImage) return; // image still uploading or failed
    // Belt + suspenders: the email gate UI already hides every entry
    // point (input box, quick replies, send button) until visitorEmail
    // exists, but if anything else calls sendMessage — devtools, host
    // integrations, automation — we still refuse to leak an
    // unattributable message into the backend.
    if (!visitorEmail) return;

    const userMessage: Message = {
      id: generateId(),
      role: "user",
      content: messageText || (readyImage ? "Sent an image" : ""),
      timestamp: new Date().toISOString(),
      image: readyImage?.uploaded?.url,
    };

    let updatedMessages = [...messages, userMessage];
    const isFirstUserMessage = messages.filter((m) => m.role === "user").length === 0;
    const newTitle = isFirstUserMessage ? (messageText || "Image").slice(0, 30) + ((messageText || "Image").length > 30 ? "..." : "") : currentConversation?.title;

    updateConversation(currentConversationId, { messages: updatedMessages, title: newTitle });

    const imageToSend = readyImage;
    const hasHumanAgent = isHumanActive(updatedMessages);
    setInput("");
    if (selectedImage) URL.revokeObjectURL(selectedImage.previewUrl);
    setSelectedImage(null);
    if (!hasHumanAgent) setConversationLoading(currentConversationId, true);

    // No need to abort — each conversation polls independently

    try {
      const requestBody: Record<string, unknown> = {
        message: userMessage.content,
        sessionId: currentConversation?.sessionId,
        widgetId,
      };

      if (visitorEmail) {
        requestBody.visitorEmail = visitorEmail;
      }

      if (imageToSend?.uploaded) {
        requestBody.image = {
          url: imageToSend.uploaded.url,
          storagePath: imageToSend.uploaded.storagePath,
          mimeType: imageToSend.mimeType,
          fileName: imageToSend.fileName,
          size: imageToSend.uploaded.size,
        };
      }

      const response = await fetch(sendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error("Failed to send message");
      const data = await response.json();
      if (!data.ok) throw new Error(data.error || "Failed to send message");

      const sessionId = data.sessionId;
      const messageId = data.messageId;

      if (messageId) {
        userMessage.id = messageId;
        updatedMessages = updatedMessages.map((m) => m === userMessage ? { ...m, id: messageId } : m);
        updateConversation(currentConversationId, { messages: updatedMessages });
      }

      if (sessionId && sessionId !== currentConversation?.sessionId) {
        updateConversation(currentConversationId, { sessionId });
      }

      if (hasHumanAgent) return;
      await pollForResponse(sessionId, messageId, updatedMessages, currentConversationId);

    } catch (error) {
      console.error("Chat error:", error);
      const errorMsg: Message = { id: generateId(), role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again later.", timestamp: new Date().toISOString() };
      updateConversation(currentConversationId, { messages: [...updatedMessages, errorMsg] });
      setConversationLoading(currentConversationId, false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // isComposing skips the Enter that confirms an IME candidate (CJK
    // input). Without this, typing "你好" + Enter would send the raw
    // pinyin partial instead of the chosen characters.
    if (e.key !== "Enter" || e.shiftKey) return;
    if ((e.nativeEvent as KeyboardEvent).isComposing) return;
    e.preventDefault();
    sendMessage();
  };

  // Keep sendMessageRef updated with latest sendMessage
  sendMessageRef.current = sendMessage;

  // Listen for external events (e.g., "Book Demo" button)
  useEffect(() => {
    const handleOpenBookDemo = (e: Event) => {
      const customEvent = e as CustomEvent<{ message?: string }>;
      const message = customEvent.detail?.message || "I want to book a demo";
      setIsOpen(true);
      // Send message after a short delay to ensure widget is open
      setTimeout(() => {
        sendMessageRef.current(message);
      }, 300);
    };
    window.addEventListener("open-book-demo", handleOpenBookDemo);
    return () => window.removeEventListener("open-book-demo", handleOpenBookDemo);
  }, []);

  // Position
  const positionStyle = position === "left" ? { left: 24 } : { right: 24 };

  // Hydration guard — see the mounted state at the top of this component.
  // Returning null on the server / first client render keeps SSR and
  // hydration outputs identical; the real widget renders once the post-
  // commit effect flips mounted to true.
  if (!mounted) return null;

  return (
    <>
      {/* Chat Window */}
      <div
        style={{
          ...styles.windowContainer,
          ...positionStyle,
          ...(isFullscreen ? styles.windowContainerFullscreen : {}),
          ...(isFullscreen && viewport ? { top: viewport.offsetTop, height: viewport.height, bottom: "auto" } : {}),
          ...(isOpen ? styles.windowContainerOpen : styles.windowContainerClosed),
          zIndex,
        }}
      >
        <div style={{
          ...styles.window,
          ...(isFullscreen ? styles.windowFullscreen : {}),
        }}>
          {/* Header */}
          <div style={{ ...styles.header, background: headerColor }}>
            {showConversationList ? (
              <div style={{ flex: 1 }}>
                <h3 style={styles.headerTitle}>Your Conversations</h3>
              </div>
            ) : (
              <>
                <button onClick={() => setShowConversationList(true)} style={styles.backBtn} aria-label="Show conversations">
                  <ChevronLeft size={20} />
                </button>
                <div style={styles.avatarContainer}>
                  <div style={styles.avatar}>
                    <img
                      src={lastHumanAgent?.avatarUrl || effectiveCompanyLogo || DEFAULT_AVATAR_LOGO}
                      alt={lastHumanAgent ? (lastHumanAgent.senderName || "Agent") : companyName}
                      style={(lastHumanAgent?.avatarUrl || effectiveCompanyLogo) ? styles.avatarImgCustom : styles.avatarImgDefault}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  </div>
                  <div style={{ ...styles.avatarStatus, borderColor: headerColor }} />
                </div>
                <div style={styles.headerInfo}>
                  <h3 style={styles.headerTitle}>{lastHumanAgent ? (lastHumanAgent.senderName || "Support Agent") : companyName}</h3>
                  <p style={styles.headerSubtitle}>{lastHumanAgent ? "Live support" : "Usually replies instantly"}</p>
                </div>
              </>
            )}

            {/* Menu */}
            <div style={styles.menuContainer} ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                style={{ ...styles.menuBtn, ...(showMenu ? styles.menuBtnOpen : {}) }}
                aria-label={showMenu ? "Close menu" : "More options"}
              >
                {showMenu ? <ChevronUp size={20} /> : <Menu size={20} />}
              </button>
              {showMenu && (
                <div style={styles.menuDropdown}>
                  <button onClick={() => { toggleSound(); setShowMenu(false); }} style={styles.menuItem}>
                    {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                    {soundEnabled ? "Mute sound" : "Unmute sound"}
                  </button>
                  {!showConversationList && (
                    <>
                      <button onClick={() => { exportConversation(); setShowMenu(false); }} style={styles.menuItem}>
                        <Download size={16} /> Export chat
                      </button>
                      {effectiveContactEmail && (
                        <button onClick={() => { openEmailContact(); setShowMenu(false); }} style={styles.menuItem}>
                          <Mail size={16} /> Email us
                        </button>
                      )}
                    </>
                  )}
                  <button onClick={() => { setIsFullscreen(!isFullscreen); setShowMenu(false); }} style={styles.menuItem}>
                    {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    {isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                  </button>
                  {visitorEmail && (
                    <button onClick={() => { setEmailInput(visitorEmail); setEmailError(""); setVisitorEmail(null); try { localStorage.removeItem(EMAIL_KEY); } catch {} setShowMenu(false); }} style={styles.menuItem}>
                      <UserRoundPen size={16} />
                      {visitorEmail.length > 20 ? visitorEmail.slice(0, 20) + "…" : visitorEmail}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Mobile fullscreen close button */}
            {isMobile && isFullscreen && (
              <button
                onClick={() => setIsOpen(false)}
                style={styles.backBtn}
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            )}
          </div>

          {!visitorEmail ? (
            <div style={{
              display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center",
              flex: 1, padding: "40px 24px", textAlign: "center" as const, background: "#f9fafb",
            }}>
              <Mail size={36} color={headerColor} style={{ marginBottom: 16 }} />
              <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600, color: "#1a1a2e" }}>Before we start</h3>
              <p style={{ margin: "0 0 24px", fontSize: 14, color: "#6b7280" }}>Please enter your email so we can follow up</p>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !(e.nativeEvent as KeyboardEvent).isComposing) handleEmailSubmit(); }}
                placeholder="you@example.com"
                style={{
                  width: "100%", padding: "10px 14px", fontSize: 16, border: emailError ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
                  borderRadius: 8, outline: "none", marginBottom: 4, boxSizing: "border-box" as const,
                }}
              />
              {emailError && <p style={{ margin: "0 0 8px", fontSize: 12, color: "#ef4444", alignSelf: "flex-start" }}>{emailError}</p>}
              <button
                onClick={handleEmailSubmit}
                style={{
                  width: "100%", padding: "10px 0", marginTop: emailError ? 8 : 16, fontSize: 14, fontWeight: 600,
                  color: "#fff", background: headerColor, border: "none", borderRadius: 8, cursor: "pointer",
                }}
              >
                Start Chat
              </button>
            </div>
          ) : showConversationList ? (
            <div style={styles.conversationList} data-lenis-prevent>
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv.id)}
                  style={{ ...styles.conversationItem, ...(conv.id === currentConversationId ? styles.conversationItemActive : {}) }}
                >
                  <MessageSquare size={20} style={styles.conversationIcon} />
                  <div style={styles.conversationInfo}>
                    <p style={styles.conversationTitle}>{conv.title}</p>
                    <p style={styles.conversationTime}>{formatTime(conv.updatedAt)}</p>
                  </div>
                </button>
              ))}
              <div style={styles.newConversationContainer}>
                <button onClick={startNewConversation} style={{ ...styles.newConversationBtn, background: headerColor }}>
                  <Plus size={20} /> Start New Conversation
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div style={styles.messagesContainer} data-lenis-prevent>
                {messages.map((message, index) => (
                  <div key={message.id}>
                    {/* Human takeover divider */}
                    {message.isHuman && !messages.slice(0, index).some((m) => m.isHuman) && (
                      <div style={styles.divider}>
                        <div style={styles.dividerLine} />
                        <p style={styles.dividerText}>Transferred to {message.senderName || "live support"}</p>
                        <div style={styles.dividerLine} />
                      </div>
                    )}

                    <div style={{ ...styles.messageRow, ...(message.role === "user" ? styles.messageRowUser : styles.messageRowAssistant) }}>
                      {message.role === "assistant" ? (
                        <div style={styles.assistantMessageWrapper}>
                          <img
                            src={message.isHuman && message.avatarUrl ? message.avatarUrl : (effectiveCompanyLogo || DEFAULT_AVATAR_LOGO)}
                            alt={message.isHuman ? (message.senderName || "Agent") : companyName}
                            style={(message.isHuman && message.avatarUrl) || effectiveCompanyLogo ? styles.messageAvatarCustom : styles.messageAvatarDefault}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                          />
                          <div style={styles.messageContent}>
                            <p style={{ ...styles.messageSender, color: message.isHuman ? "#f97316" : "#646566" }}>
                              {message.isHuman ? (message.senderName || "Support Agent") : companyName}
                            </p>
                            <div style={{ ...styles.messageBubble, ...styles.messageBubbleAssistant }}>
                              {message.image && message.image !== "[image]" && <img src={message.image} alt="Shared" style={styles.messageImage} />}
                              {message.image === "[image]" && (
                                <div style={styles.imagePlaceholder}>
                                  <ImageIcon size={20} color="#888" /> <span style={{ color: "#888", fontSize: 13 }}>Image</span>
                                </div>
                              )}
                              {message.content && message.content !== "Sent an image" && <p style={styles.messageText}>{renderTextWithLinks(message.content)}</p>}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ maxWidth: "80%", textAlign: "right" as const }}>
                          <p style={{ ...styles.messageSender, color: "#969799" }}>You</p>
                          <div style={{ ...styles.messageBubble, ...styles.messageBubbleUser, background: headerColor }}>
                            {message.image && message.image !== "[image]" && <img src={message.image} alt="Shared" style={styles.messageImage} />}
                            {message.image === "[image]" && (
                              <div style={styles.imagePlaceholder}>
                                <ImageIcon size={20} color="#888" /> <span style={{ color: "#888", fontSize: 13 }}>Image</span>
                              </div>
                            )}
                            {message.content && message.content !== "Sent an image" && <p style={styles.messageText}>{renderTextWithLinks(message.content)}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Quick replies */}
                {!isLoading && messages.filter((m) => m.role === "user").length === 0 && quickReplies.length > 0 && (
                  <div style={styles.quickRepliesContainer}>
                    {quickReplies.map((text, index) => (
                      <button key={`${index}-${text}`} onClick={() => sendMessage(text)} style={styles.quickReplyBtn}>{text}</button>
                    ))}
                  </div>
                )}

                {/* Loading */}
                {isLoading && (
                  <div style={{ ...styles.messageRow, ...styles.messageRowAssistant }}>
                    <div style={styles.assistantMessageWrapper}>
                      <img
                        src={lastHumanAgent?.avatarUrl || effectiveCompanyLogo || DEFAULT_AVATAR_LOGO}
                        alt={lastHumanAgent ? (lastHumanAgent.senderName || "Agent") : companyName}
                        style={(lastHumanAgent?.avatarUrl || effectiveCompanyLogo) ? styles.messageAvatarCustom : styles.messageAvatarDefault}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                      <div style={styles.messageContent}>
                        <p style={{ ...styles.messageSender, color: lastHumanAgent ? "#f97316" : "#646566" }}>
                          {lastHumanAgent ? (lastHumanAgent.senderName || "Support Agent") : companyName}
                        </p>
                        <div style={{ ...styles.messageBubble, ...styles.messageBubbleAssistant, ...styles.loadingBubble }}>
                          <span style={styles.loadingText}>Thinking, please wait</span>
                          <div style={styles.loadingDots}>
                            <div style={{ ...styles.loadingDot, animationDelay: "0ms" }} />
                            <div style={{ ...styles.loadingDot, animationDelay: "150ms" }} />
                            <div style={{ ...styles.loadingDot, animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={styles.inputArea}>
                {selectedImage && (
                  <div style={styles.imagePreview}>
                    <img src={selectedImage.previewUrl} alt="Selected" style={styles.previewImg} />
                    {selectedImage.uploading && (
                      <div style={styles.imageUploadingOverlay}>
                        <Loader2 size={16} style={{ animation: "lcw-spin 1s linear infinite", color: "#fff" }} />
                      </div>
                    )}
                    <button
                      onClick={() => {
                        URL.revokeObjectURL(selectedImage.previewUrl);
                        setSelectedImage(null);
                      }}
                      style={styles.removeImageBtn}
                      aria-label="Remove image"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                <div style={styles.inputRow}>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} style={styles.fileInput} />
                  <button onClick={() => fileInputRef.current?.click()} style={styles.iconBtn} aria-label="Upload image" disabled={isLoading}>
                    <ImageIcon size={20} />
                  </button>
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    style={styles.textInput}
                    disabled={isLoading}
                  />
                  <div style={styles.emojiContainer} ref={emojiRef}>
                    <button onClick={() => setShowEmoji(!showEmoji)} style={styles.iconBtn} aria-label="Add emoji">
                      <Smile size={20} />
                    </button>
                    {showEmoji && (
                      <div style={styles.emojiPicker}>
                        {EMOJI_LIST.map((emoji) => (
                          <button key={emoji} onClick={() => { insertEmoji(emoji); setShowEmoji(false); }} style={styles.emojiBtn}>{emoji}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => sendMessage()} disabled={(!input.trim() && !selectedImage?.uploaded) || isLoading || Boolean(selectedImage?.uploading)} style={{ ...styles.sendBtn, color: headerColor, opacity: (!input.trim() && !selectedImage?.uploaded) || isLoading || selectedImage?.uploading ? 0.3 : 1 }} aria-label="Send message">
                    {isLoading ? <Loader2 size={20} style={{ animation: "lcw-spin 1s linear infinite" }} /> : <Send size={20} />}
                  </button>
                </div>
                <a href="https://hirelucius.com/" target="_blank" rel="noopener noreferrer" className="lcw-powered-by" style={styles.poweredBy}>Powered by Lucius AI</a>
              </div>
            </>
          )}
        </div>
      </div>

      {/* FAB */}
      {!isFullscreen && (
        <div style={{ ...styles.fabContainer, ...positionStyle }}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            style={{
              ...styles.fab,
              background: isOpen ? "#1a1a2e" : "#ffffff",
              border: "none",
              boxShadow: fabHovered && !isOpen
                ? "0 0 0 2px #e5e3df, 0 0 0 4px #1a1a2e, 0 8px 20px rgba(0,0,0,0.15)"
                : "0 0 0 2px #e5e3df, 0 4px 12px rgba(0,0,0,0.1)",
              transform: fabHovered && !isOpen ? "scale(1.05)" : "scale(1)",
            }}
            onMouseEnter={() => setFabHovered(true)}
            onMouseLeave={() => setFabHovered(false)}
            aria-label={isOpen ? "Close chat" : "Open chat"}
          >
            {isOpen ? (
              <X size={24} color="white" />
            ) : (
              <img
                src={effectiveFabLogo || effectiveCompanyLogo || DEFAULT_AVATAR_LOGO}
                alt={companyName}
                style={(effectiveFabLogo || effectiveCompanyLogo) ? styles.fabLogoCustom : styles.fabLogoDefault}
              />
            )}
          </button>
        </div>
      )}
    </>
  );
}
